<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import BaseDialog from '~/components/molecules/BaseDialog.vue'
import InputWithLabel from '~/components/molecules/InputWithLabel.vue'
import { useCurrentLocationAccount } from '~/composables/useCurrentLocationAccount'
import { useLoading } from '~/composables/useLoading'
import { useOperator } from '~/composables/useOperator'
import { useOperatorValidator } from '~/composables/useOperatorValidator'
import type { Operator } from '~/entities/operator'
import { LimitExceededError } from '~/repositories/error'

const { t } = useI18n()
const { withLoading } = useLoading()
const { currentLocationAccount } = useCurrentLocationAccount()
const { createOperator, updateOperator } = useOperator()

const props = defineProps<{ isActive: boolean; operator?: Operator }>()

const emit = defineEmits<{
  (e: 'update:isActive', value: boolean): void
  (e: 'done', operator: Operator): void
  (e: 'error', message: string): void
}>()

const close = () => {
  emit('update:isActive', false)
}

const title = computed(() => (props.operator ? t('個人設定') : t('作業者の追加')))

const name = ref(props.operator?.name ?? '')
const namePronunciation = ref(props.operator?.name_pronunciation ?? '')
const { operatorNameErrorMessage, operatorNamePronunciationErrorMessage, isValid } =
  useOperatorValidator(name, namePronunciation)

const canSave = isValid

const save = async () => {
  if (!currentLocationAccount.value) return
  if (!canSave.value) return

  const params = {
    name: name.value,
    name_pronunciation: namePronunciation.value,
    location_id: currentLocationAccount.value.location.id,
    organization_id: currentLocationAccount.value.organization.id,
  }

  try {
    await withLoading(async () => {
      const operator = props.operator
        ? await updateOperator(props.operator.id, params)
        : await createOperator(params)
      emit('done', operator)
    })
  } catch (e) {
    if (e instanceof LimitExceededError) {
      emit('error', t('上限に到達したため、登録できませんでした。'))
      return
    }
    throw e
  } finally {
    close()
  }
}
</script>

<template>
  <BaseDialog
    :is-active="isActive"
    :title="title"
    :buttons="{
      ok: { text: $t('保存'), disabled: !canSave },
      cancel: { text: $t('キャンセル') },
    }"
    @close="close"
    @cancel="close"
    @ok="save"
  >
    <template #content>
      <section class="body">
        <div class="input-area">
          <div class="input-block">
            <InputWithLabel
              v-model="name"
              :label="$t('名前')"
              :error-message="operatorNameErrorMessage"
            />
          </div>
          <div class="input-block">
            <InputWithLabel
              v-model="namePronunciation"
              :label="$t('読み方')"
              :error-message="operatorNamePronunciationErrorMessage"
            />
          </div>
        </div>
      </section>
    </template>
  </BaseDialog>
</template>

<style scoped lang="scss">
.body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  overflow: auto;
  .input-area {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
</style>
